import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {
  WEEK_PRESETS,
  describeWeek,
  presetOf,
  setDayTime,
  weekRows,
  type WeekPreset,
} from '@/entities/attendance/schedule';
import type {DayTimeInput} from '@/entities/attendance/types';
import {formatDayMinutes} from '@/shared/lib/format';
import {IconButton} from '@/shared/ui/button';
import {ChoiceChips} from '@/shared/ui/segmented';
import {AppText} from '@/shared/ui/text';
import {TimeField} from '@/shared/ui/time-field';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Неделя графика одной таблицей — общий редактор для отдела и для личного графика.
 *
 * Раньше «пн–пт 9–18, сб 9–14, вс выходной» собиралось в трёх местах: флажки дней,
 * общее время и отдельный режим «По дням» со своим списком. Здесь всё в строке дня:
 * нажал на день — он стал выходным или рабочим; поменял время в строке — у дня своё
 * время, и строка это помечает; «↺» возвращает её к обычному.
 *
 * Хранение прежнее (маска + общее время + отличия от него), таблица только читает и
 * пишет его — сервер и отчёты ничего не замечают. Сверху — итог одной строкой, чтобы
 * перед «Сохранить» было видно, что получилось, не читая семь строк.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  summary: {borderRadius: radii.md, padding: spacing(3), gap: 2},
  common: {flexDirection: 'row', gap: spacing(3)},
  flex: {flex: 1},
  table: {gap: spacing(2)},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(2), minHeight: 44},
  day: {
    width: 44,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.md,
    borderWidth: 1,
  },
  dash: {paddingHorizontal: 2},
  reset: {width: 32},
});

export type WeekValue = {
  mask: number;
  /** Общее время; оба `null` — у графика нет времени, только рабочие дни. */
  start: number | null;
  end: number | null;
  dayTimes: DayTimeInput[];
};

export function WeekScheduleEditor({
  value,
  onChange,
  disabled = false,
}: {
  value: WeekValue;
  onChange: (next: WeekValue) => void;
  disabled?: boolean;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const {mask, start, end, dayTimes} = value;
  const timed = start != null && end != null;
  const rows = weekRows(mask, start, end, dayTimes);
  const dayLabel = (d: number) => t(`home.person.weekday.${d}`);
  const summary = describeWeek(
    rows,
    dayLabel,
    t('schedule.week.off'),
    m => formatDayMinutes(m) ?? '',
  );

  const set = (next: Partial<WeekValue>) => onChange({...value, ...next});
  const toggleDay = (d: number) => set({mask: mask ^ (1 << d)});
  const setRow = (d: number, s: number, e: number) =>
    timed && set({dayTimes: setDayTime(dayTimes, d, s, e, start, end)});

  const presets: ReadonlyArray<{value: WeekPreset; label: string}> = [
    {value: 'weekdays', label: t('schedule.week.presetWeekdays')},
    {value: 'sixDays', label: t('schedule.week.presetSixDays')},
    {value: 'everyDay', label: t('schedule.week.presetEveryDay')},
  ];

  return (
    <View style={styles.root} testID="week-schedule">
      <View style={[styles.summary, {backgroundColor: p.muted}]}>
        <AppText variant="caption">{t('schedule.week.summary')}</AppText>
        <AppText variant="label" testID="week-summary">
          {mask === 0 ? t('schedule.week.none') : summary}
        </AppText>
      </View>

      <ChoiceChips
        options={presets}
        // Своя неделя — ни один вариант не подсвечен.
        value={(presetOf(mask) ?? '') as WeekPreset}
        onChange={preset => set({mask: WEEK_PRESETS[preset]})}
        disabled={disabled}
        testID="week-presets"
      />

      {timed ? (
        <View style={styles.common}>
          <TimeField
            label={t('schedule.week.usualFrom')}
            minutes={start}
            onChange={m => set({start: m})}
            style={styles.flex}
          />
          <TimeField
            label={t('schedule.week.usualTo')}
            minutes={end}
            onChange={m => set({end: m})}
            style={styles.flex}
          />
        </View>
      ) : null}

      <View style={styles.table}>
        {rows.map(r => (
          <View key={r.weekday} style={styles.row} testID={`week-row-${r.weekday}`}>
            <Pressable
              onPress={disabled ? undefined : () => toggleDay(r.weekday)}
              accessibilityRole="switch"
              accessibilityState={{checked: r.working, disabled}}
              accessibilityLabel={t('schedule.week.dayToggle', {day: dayLabel(r.weekday)})}
              style={({pressed}) => [
                styles.day,
                {
                  backgroundColor: r.working ? p.primary : pressed ? p.muted : p.card,
                  borderColor: r.working ? p.primary : p.border,
                },
              ]}>
              <AppText
                variant="label"
                color={r.working ? p.primaryForeground : p.mutedForeground}>
                {dayLabel(r.weekday)}
              </AppText>
            </Pressable>

            {!r.working ? (
              <AppText variant="caption" style={styles.flex}>
                {t('schedule.week.off')}
              </AppText>
            ) : !timed || r.startMinutes == null || r.endMinutes == null ? (
              <AppText variant="caption" style={styles.flex}>
                {t('schedule.week.working')}
              </AppText>
            ) : (
              <>
                <TimeField
                  accessibilityLabel={t('schedule.week.dayFrom', {day: dayLabel(r.weekday)})}
                  minutes={r.startMinutes}
                  onChange={m => setRow(r.weekday, m, r.endMinutes!)}
                  style={styles.flex}
                />
                <AppText variant="caption" style={styles.dash}>
                  –
                </AppText>
                <TimeField
                  accessibilityLabel={t('schedule.week.dayTo', {day: dayLabel(r.weekday)})}
                  minutes={r.endMinutes}
                  onChange={m => setRow(r.weekday, r.startMinutes!, m)}
                  style={styles.flex}
                />
                <View style={styles.reset}>
                  {r.custom ? (
                    <IconButton
                      icon="refresh"
                      size={32}
                      label={t('schedule.week.reset', {day: dayLabel(r.weekday)})}
                      onPress={() => setRow(r.weekday, start, end)}
                      disabled={disabled}
                    />
                  ) : null}
                </View>
              </>
            )}
          </View>
        ))}
      </View>

      <AppText variant="caption">
        {t(timed ? 'schedule.week.hint' : 'schedule.week.hintNoTimes')}
      </AppText>
    </View>
  );
}
